import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import { AttributesNumberErrorModal } from "../../../../../shared/modals/AttributesNumberErrorModal";
import { useGame } from "../../../../../../contexts/game/GameContext";
import { useProject } from "../../../../../../contexts/project/ProjectContext";
import { useStep } from "../../../../../../contexts/step/StepContext";
import { GameKind } from "../../../../../../gql/graphql";
import {
  QUICKRANK_CONFIGURATION_UPDATE,
  RANKIFIER_CONFIGURATION_UPDATE,
  SWIPER_CONFIGURATION_UPDATE,
  TRIVIA_CONFIGURATION_UPDATE,
  WORDLE_CONFIGURATION_UPDATE,
  WORDOPOLIS_CONFIGURATION_UPDATE,
} from "../../../../../../graphql/mutations/GameMutation";
import { ShareProjectModal } from "../../../../../shared/modals/ShareProjectModal";
import { Form } from "./Form";
import { ConfigChangeData } from "./Form/Form";

export type GameResultsDataKeys = "showWelcomePage" | "showFeedbackPage" | "showGameResults" | "showRatingScale";

export const GameResults = () => {
  const { currentGame } = useGame();
  const { project } = useProject();
  const { isShareModalOpen, isAttributesNumberErrorModalOpen } = useStep();
  const navigate = useNavigate();
  const [updateSwiperConfig] = useMutation(SWIPER_CONFIGURATION_UPDATE);
  const [updateRankifierConfig] = useMutation(RANKIFIER_CONFIGURATION_UPDATE);
  const [updateQuickrankConfig] = useMutation(QUICKRANK_CONFIGURATION_UPDATE);
  const [updateWordopolisConfig] = useMutation(WORDOPOLIS_CONFIGURATION_UPDATE);
  const [updateTriviaConfig] = useMutation(TRIVIA_CONFIGURATION_UPDATE);
  const [updateWordleConfig] = useMutation(WORDLE_CONFIGURATION_UPDATE);

  const mutationFunctions: {
    [key in GameKind]: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      mutation: any;
      idKey: string;
      dataKeys: GameResultsDataKeys[];
    };
  } = {
    [GameKind.Swiper]: {
      mutation: updateSwiperConfig,
      idKey: "swiperConfigurationId",
      dataKeys: ["showWelcomePage", "showFeedbackPage", "showGameResults"],
    },
    [GameKind.Rankifier]: {
      mutation: updateRankifierConfig,
      idKey: "rankifierConfigurationId",
      dataKeys: ["showWelcomePage", "showFeedbackPage", "showGameResults"],
    },
    [GameKind.QuickRank]: {
      mutation: updateQuickrankConfig,
      idKey: "quickRankConfigurationId",
      dataKeys: ["showWelcomePage", "showFeedbackPage", "showGameResults"],
    },
    [GameKind.Wordopolis]: {
      mutation: updateWordopolisConfig,
      idKey: "wordopolisConfigurationId",
      dataKeys: ["showWelcomePage", "showRatingScale"],
    },
    [GameKind.Trivia]: {
      mutation: updateTriviaConfig,
      idKey: "triviaConfigurationId",
      dataKeys: ["showWelcomePage", "showFeedbackPage"],
    },
    [GameKind.Wordle]: {
      mutation: updateWordleConfig,
      idKey: "wordleConfigurationId",
      dataKeys: ["showWelcomePage", "showFeedbackPage"],
    },
  };

  if (!currentGame || !project) {
    return null;
  }

  const { mutation, idKey, dataKeys } = mutationFunctions[currentGame.kind];

  const handleConfigChange = (data: ConfigChangeData) => {
    mutation({
      variables: {
        [idKey]: currentGame.configuration.id,
        data: dataKeys.reduce((acc, key) => ({ ...acc, [key]: data[key] }), {}),
      },
    });
  };

  const closeShareModal = () => {
    navigate("/");
  };

  return (
    <>
      <Form
        game={currentGame}
        key={currentGame.id}
        dataKeys={dataKeys}
        onConfigChange={handleConfigChange}
        projectId={project.id}
      />
      {isShareModalOpen && <ShareProjectModal project={project} onClose={closeShareModal} />}
      {isAttributesNumberErrorModalOpen && <AttributesNumberErrorModal />}
    </>
  );
};
